// Grand Gather Registry — Notification Sound Engine
// Generates sounds using Web Audio API (no external files needed)

let audioCtx = null;

function getAudioContext() {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

// Attention-grabbing alert — like WhatsApp/Facebook messenger ping
// Fast triplet pattern with rich harmonics that demands attention
export async function playNotificationSound() {
  try {
    const ctx = await getAudioContext();
    const now = ctx.currentTime;

    // Three rapid notes: D6→A5→F6 (quick, urgent feel)
    const notes = [
      { freq: 1175, time: 0 },     // D6
      { freq: 880, time: 0.07 },   // A5
      { freq: 1397, time: 0.14 },  // F6
    ];

    notes.forEach(({ freq, time }) => {
      // Main tone — triangle wave for richer sound than pure sine
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + time);
      gain.gain.setValueAtTime(0, now);
      gain.gain.setValueAtTime(0.35, now + time);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + time);
      osc.stop(now + time + 0.18);

      // Harmonic overtone — sine an octave up for brightness
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(freq * 2, now + time);
      gain2.gain.setValueAtTime(0, now);
      gain2.gain.setValueAtTime(0.12, now + time);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + time + 0.12);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + time);
      osc2.stop(now + time + 0.12);
    });

    // Second burst after a tiny gap — single punchy note
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'triangle';
    osc3.frequency.setValueAtTime(1568, now + 0.26); // G6
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.setValueAtTime(0.3, now + 0.26);
    gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.start(now + 0.26);
    osc3.stop(now + 0.42);

    // Overtone for the final note
    const osc4 = ctx.createOscillator();
    const gain4 = ctx.createGain();
    osc4.type = 'sine';
    osc4.frequency.setValueAtTime(3136, now + 0.26); // G7 shimmer
    gain4.gain.setValueAtTime(0, now);
    gain4.gain.setValueAtTime(0.08, now + 0.26);
    gain4.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
    osc4.connect(gain4);
    gain4.connect(ctx.destination);
    osc4.start(now + 0.26);
    osc4.stop(now + 0.38);
  } catch (e) {
    // Audio not available — fail silently
  }
}

// Success sound — bright ascending chime (for confirmations, completed actions)
export function playSuccessSound() {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => playSuccessTones(ctx));
    } else {
      playSuccessTones(ctx);
    }
  } catch (e) {}
}

function playSuccessTones(ctx) {
  const now = ctx.currentTime;
  const notes = [523, 659, 784]; // C5 → E5 → G5 (major chord arpeggio)
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + i * 0.08);
    gain.gain.setValueAtTime(0, now);
    gain.gain.setValueAtTime(0.2, now + i * 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.4);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + i * 0.08);
    osc.stop(now + i * 0.08 + 0.4);
  });
}

// Error / warning sound — soft descending tone
export function playErrorSound() {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => playErrorTone(ctx));
    } else {
      playErrorTone(ctx);
    }
  } catch (e) {}
}

function playErrorTone(ctx) {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(440, now); // A4
  osc.frequency.exponentialRampToValueAtTime(330, now + 0.25); // drops to E4
  gain.gain.setValueAtTime(0.2, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.35);
}

// Single clear chime — for GiGi popup on landing page
export function playSingleChime() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now); // A5 — bright, friendly
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
  } catch (e) {}
}

// Unlock AudioContext on first user interaction (browser autoplay policy)
export function unlockAudio() {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, ctx.currentTime);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.001);
      });
    }
  } catch (e) {}
}

// Message received sound — single soft pop (for chat messages)
export function playMessageSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now); // A5
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.15);
  } catch (e) {}
}
